import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { EventManager, AlertService } from 'app/shared/services';
import { AlertComponent } from 'app/shared/alert/alert.component';

import { AccountService } from 'app/core';

import {
    ExecutionPhase,
    IExecutionRow,
    IQueueRow,
    IRecentExecutionRow
} from 'app/shared/model/FDERunningAccountService/execution.model';
import { RunningAccountService } from 'app/entities/FDERunningService/running-accounts/running-account.service';

const REFRESH_INTERVAL_MS = 1000;
const RECENT_REFRESH_INTERVAL_MS = 10000;
const STALE_PING_SECONDS = 6;
const PHASE_ORDER: ExecutionPhase[] = ['RUNNING', 'WAITING_USER', 'DISPATCHED', 'QUEUED', 'NO_SIGNAL'];

@Component({
    selector: 'app-running-account',
    standalone: true,
    imports: [CommonModule, AlertComponent],
    templateUrl: './running-account.component.html',
    styleUrls: ['./running-account.component.css']})
export class RunningAccountsComponent implements OnInit, OnDestroy {
    currentAccount: any;
    executions: IExecutionRow[] = [];
    queue: IQueueRow[] = [];
    recent: IRecentExecutionRow[] = [];
    eventSubscriber: Subscription;
    updateTableInterval;
    recentInterval;

    constructor(
        protected extractorRunnerService: RunningAccountService,
        protected jhiAlertService: AlertService,
        protected accountService: AccountService,
        protected eventManager: EventManager
    ) {}

    ngOnInit() {
        this.loadAll();
        this.accountService.identity().then(account => {
            this.currentAccount = account;
        });
        this.eventSubscriber = this.eventManager.subscribe('extractorRunnerListModification', () => this.loadAll());
        this.updateTableInterval = setInterval(() => this.loadInFlight(), REFRESH_INTERVAL_MS);
        this.recentInterval = setInterval(() => this.loadRecent(), RECENT_REFRESH_INTERVAL_MS);
    }

    ngOnDestroy() {
        this.eventManager.destroy(this.eventSubscriber);
        clearInterval(this.updateTableInterval);
        clearInterval(this.recentInterval);
    }

    loadAll() {
        this.loadInFlight();
        this.loadRecent();
    }

    loadInFlight() {
        this.extractorRunnerService.queryExecutions().subscribe(
            (res: HttpResponse<IExecutionRow[]>) => (this.executions = this.sortByPhase(res.body || [])),
            () => (this.executions = [])
        );
        this.extractorRunnerService.queryQueue().subscribe(
            (res: HttpResponse<IQueueRow[]>) => (this.queue = res.body || []),
            () => (this.queue = [])
        );
    }

    loadRecent() {
        this.extractorRunnerService.queryRecent().subscribe(
            (res: HttpResponse<IRecentExecutionRow[]>) => (this.recent = res.body || []),
            () => (this.recent = [])
        );
    }

    trackByExecution(index: number, row: IExecutionRow): string {
        return row.accountCode || String(index);
    }

    trackByQueueRow(index: number, row: IQueueRow): number {
        return row.id ?? index;
    }

    trackByRecent(index: number, row: IRecentExecutionRow): string {
        return (row.accountCode || index) + '_' + (row.finishedAt || '');
    }

    outcomeLabel(row: IRecentExecutionRow): string {
        switch (row.externalStatus) {
            case 'UPDATED_SUCCESSFULLY':
                return 'Success';
            case 'UPDATED_ERROR':
                return 'Error';
            default:
                return 'Cancelled';
        }
    }

    outcomeClass(row: IRecentExecutionRow): string {
        switch (row.externalStatus) {
            case 'UPDATED_SUCCESSFULLY':
                return 'out-success';
            case 'UPDATED_ERROR':
                return 'out-error';
            default:
                return 'out-cancelled';
        }
    }

    finishedText(row: IRecentExecutionRow): string {
        if (row.finishedSecondsAgo === null || row.finishedSecondsAgo === undefined) {
            return '';
        }
        return this.formatDuration(row.finishedSecondsAgo) + ' ago';
    }

    phaseLabel(phase: ExecutionPhase): string {
        switch (phase) {
            case 'QUEUED':
                return 'In queue';
            case 'DISPATCHED':
                return 'Sent to runner';
            case 'RUNNING':
                return 'Running';
            case 'WAITING_USER':
                return 'Waiting for user';
            default:
                return 'No signal';
        }
    }

    phaseClass(phase: ExecutionPhase): string {
        switch (phase) {
            case 'QUEUED':
                return 'ph-queued';
            case 'DISPATCHED':
                return 'ph-dispatched';
            case 'RUNNING':
                return 'ph-running';
            case 'WAITING_USER':
                return 'ph-waiting';
            default:
                return 'ph-nosignal';
        }
    }

    phaseHint(phase: ExecutionPhase): string {
        switch (phase) {
            case 'QUEUED':
                return 'Waiting for a runner slot';
            case 'DISPATCHED':
                return 'Published to the runner, extraction has not started yet';
            case 'RUNNING':
                return 'The runner thread is pinging while it works';
            case 'WAITING_USER':
                return 'The run is paused until the user answers the form. This time is not counted as execution';
            default:
                return 'Status says it is in flight, but neither the queue, the runner nor the watchdog knows about it';
        }
    }

    sourceLabel(row: IQueueRow): string {
        return row.source === 'USER_REFRESH' ? 'User refresh' : 'Nightly refresh';
    }

    sourceClass(row: IQueueRow): string {
        return row.source === 'USER_REFRESH' ? 'src-user' : 'src-nightly';
    }

    isStale(row: IExecutionRow): boolean {
        return row.alive && (row.secondsSinceLastPing ?? 0) > STALE_PING_SECONDS;
    }

    pingText(row: IExecutionRow): string {
        if (!row.alive) {
            return '—';
        }
        return (row.secondsSinceLastPing ?? 0) + 's ago';
    }

    timeText(seconds: number): string {
        if (seconds === null || seconds === undefined) {
            return '—';
        }
        return this.formatDuration(seconds);
    }

    timingTitle(total: number, queued: number, waiting: number, execution: number): string {
        if (total === null || total === undefined) {
            return '';
        }
        return 'Total ' + this.formatDuration(total)
            + ' = queue ' + this.formatDuration(queued ?? 0)
            + ' + execution ' + this.formatDuration(execution ?? 0)
            + ' + waiting for user ' + this.formatDuration(waiting ?? 0);
    }

    shortAccountCode(accountCode: string): string {
        if (!accountCode || accountCode.length <= 10) {
            return accountCode;
        }
        return accountCode.substring(0, 4) + '...' + accountCode.substring(accountCode.length - 3);
    }

    copyToClipboard(value: string) {
        if (!value) {
            return;
        }
        navigator.clipboard.writeText(value).then(
            () => this.jhiAlertService.success('Account code copied', null, null),
            () => this.jhiAlertService.error('Unable to copy the account code', null, null)
        );
    }

    formatDuration(seconds: number): string {
        if (seconds === null || seconds === undefined) {
            return '';
        }
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remaining = seconds % 60;

        let result = '';
        if (hours > 0) {
            result += hours + 'h ';
        }
        if (minutes > 0 || hours > 0) {
            result += minutes + 'm ';
        }
        return result + remaining + 's';
    }

    private sortByPhase(rows: IExecutionRow[]): IExecutionRow[] {
        return rows.slice().sort((left, right) => {
            const byPhase = PHASE_ORDER.indexOf(left.phase) - PHASE_ORDER.indexOf(right.phase);
            if (byPhase !== 0) {
                return byPhase;
            }
            return (right.inFlightSeconds ?? 0) - (left.inFlightSeconds ?? 0);
        });
    }
}
